import React, { useEffect, useMemo } from "react";
import { Box, CardContent, Grid } from "@mui/material";
import { searchEmailNotifications } from "../../../../../axios/api/services";
import {
  BorderGreyBox,
  CardSpg,
  CreatedDateFilter,
  handleSubmitValidationFail,
  isNotEmpty,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import useSearchTableControl from "../../../../../hooks/portal/search/useSearchTableControl";
import SearchFilters from "../../../common/search/filters/SearchFilters";
import { SEARCH_FILTERS_GROUP } from "../../../../../config/search/filters/groupsConfig";
import { FormProvider } from "react-hook-form";
import { EmailNotificationFilterDetails } from "../../../../../types/emailNotificationTypes";
import { createEmailNotificationFilterValidationSchema } from "../../../../../yup/schema/email/notification/notificationFilterValidationSchema";
import { emailNotificationFilterInitialValues } from "../../../../../init/emailNotificationFilterInitialValues";
import { useTranslation } from "react-i18next";
import EmailNotificationsList from "./EmailNotificationsList";
import { useStore } from "react-redux";
import RecipientsFilter from "../../../common/search/filters/definition/RecipientsFilter";
import EmailNotificationStatusFilter from "../../../common/search/filters/definition/status/EmailNotificationStatusFilter";
import SentDateFilter from "../../../common/search/filters/definition/SentDateFilter";
import { RootState } from "../../../../../store/redux/store";

const EmailNotificationsFilters = () => {
  return (
    <Grid container spacing={5} px={2} py={6}>
      <RecipientsFilter />
      <CreatedDateFilter />
      <EmailNotificationStatusFilter />
      <SentDateFilter />
    </Grid>
  );
};

const EmailNotifications = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.EMAIL_NOTIFICATIONS;

  const { methods } = useReactHookForm<EmailNotificationFilterDetails>({
    defaultValues: emailNotificationFilterInitialValues,
    validationSchema: createEmailNotificationFilterValidationSchema,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.portalSearchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: emailNotificationFilterInitialValues,
    filterData: searchEmailNotifications,
    callAfterResetFilters: true,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t, undefined),
        )}
      >
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <Box>
              <BorderGreyBox>
                <SearchFilters
                  isSubmitBtnDisabled={control.isSubmitBtnDisabled}
                  isResetBtnDisabled={control.isResetBtnDisabled}
                  handleResetFilters={control.handleResetFilters}
                >
                  {useMemo(
                    () => (
                      <EmailNotificationsFilters />
                    ),
                    [],
                  )}
                </SearchFilters>
              </BorderGreyBox>
              {control.showList && (
                <EmailNotificationsList
                  records={control.records}
                  total={control.total}
                  isLoading={control.isLoading}
                  onPageOrOrderChange={control.handlePageOrOrderChange}
                  blockTable={control.blockTable}
                  group={group}
                  filterFn={control.fetch}
                />
              )}
            </Box>
          </CardContent>
        </CardSpg>
      </form>
    </FormProvider>
  );
};

export default EmailNotifications;
